import { lazy, Suspense, useEffect, useRef } from "react";
import { Navigate } from "react-router";
import { FullScreenLoading } from "../components/full-screen-loading.tsx";
import { ArgusOnboarding } from "../components/onboarding/argus-onboarding.tsx";
import { useOnboardingMode } from "../hooks/use-onboarding-mode.ts";
import { useOrgFlags } from "../hooks/use-org-flags.ts";
import { track } from "../lib/analytics.ts";

// Lazy-load BetterOnboarding so the legacy wizard stays out of the
// default bundle for the (vast majority of) users on the v2 Argus flow.
const BetterOnboarding = lazy(() =>
  import("../components/onboarding/better-onboarding.tsx").then((m) => ({
    default: m.BetterOnboarding,
  })),
);

/**
 * `/onboarding` route. Phase 3 swaps the Phase 2 stub for the real
 * `<ArgusOnboarding />` shell (hero + provision/status machine + Skip).
 *
 * Behavior:
 *  - If flags are still loading (no cache + no fetch yet): full-screen spinner.
 *  - If onboarding is genuinely completed (auto / user_action): redirect to `/`.
 *  - If the user previously skipped, still allow re-entry here — the sidebar
 *    `Onboarding` link is the primary re-entry path and it should actually work.
 *  - Otherwise: render the per-device onboarding mode (default `argus`; `manual`
 *    is an opt-in via the hero's "Use manual onboarding" button — see F2).
 */
export default function OnboardingPage() {
  const { isLoading, isCompleted, onboarding } = useOrgFlags();
  const [mode] = useOnboardingMode();
  const completionTrackedRef = useRef(false);

  // Fire `onboarding_completed` once when the org flips to a non-skipped
  // completion. Idempotent across re-renders via ref.
  useEffect(() => {
    if (
      isCompleted &&
      onboarding.completion_reason !== "skipped" &&
      !completionTrackedRef.current
    ) {
      completionTrackedRef.current = true;
      track("onboarding_completed", {
        completion_reason: onboarding.completion_reason,
      });
    }
  }, [isCompleted, onboarding.completion_reason]);

  if (isLoading) {
    return <FullScreenLoading message="Loading your experience..." />;
  }

  if (isCompleted && onboarding.completion_reason !== "skipped") {
    return <Navigate to="/" replace />;
  }

  if (mode === "manual") {
    return (
      <Suspense fallback={<FullScreenLoading message="Loading your experience..." />}>
        <BetterOnboarding />
      </Suspense>
    );
  }

  return <ArgusOnboarding />;
}
